import { useMemo, useState } from 'react'
import {
  Listbox as HListbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { nanoid } from 'nanoid'
import styled, { css } from 'styled-components'

type ListboxProps = Parameters<typeof HListbox>[0]
type ListboxButtonProps = Parameters<typeof ListboxButton>[0]
type ListboxOptionsProps = Parameters<typeof ListboxOptions>[0]
type ListboxOptionProps = Parameters<typeof ListboxOption>[0]

const TypedListbox = (props: ListboxProps) => <HListbox as="div" {...props} />
const TypedListboxButton = (props: ListboxButtonProps) => (
  <ListboxButton as="button" {...props} />
)
const TypedListboxOptions = (props: ListboxOptionsProps) => (
  <ListboxOptions as="div" {...props} />
)
const TypedListboxOption = (props: ListboxOptionProps) => (
  <ListboxOption as="div" {...props} />
)

const StyledListbox = styled(TypedListbox)<{ $isOpen: boolean }>`
  position: relative;
  font-size: var(--default-text);
  font-family: ${({ theme }) => theme.fonts.primary};
  background-color: ${({ theme }) => theme.colors.surface.subdued};
  border: 1px solid ${({ theme }) => theme.colors.button.secondaryBorder};
  border-radius: 4px;
  padding: 0 16px;
  width: 100%;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom: none;
    `}
`

const StyledButton = styled(TypedListboxButton)`
  font-size: var(--default-text);
  font-family: ${({ theme }) => theme.fonts.primary};
  padding: 16px 8px;
  box-sizing: border-box;
  border: none;
  width: 100%;
  /* color */
  background-color: transparent;
  /* maybe center */
  text-align: left;
`

const StyledOptions = styled(TypedListboxOptions)`
  position: absolute;
  z-index: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  list-style: none;
  border: 1px solid ${({ theme }) => theme.colors.button.secondaryBorder};
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  width: calc(100% + 2px);
  left: -1px;
  padding-bottom: 8px;
  background-color: ${({ theme }) => theme.colors.surface.subdued};
  margin: 0;
  padding-left: 0;
`

const OptionContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 8px 24px;
  cursor: pointer;
  /* hover state */
  &:hover {
    background-color: white;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25em;
  width: 100%;

  > label {
    color: ${({ theme }) => theme.colors.font.regular};
    font-size: var(--small-text);
    font-weight: 700;
  }
`

export const Separator = styled.div.attrs({ role: 'separator' })<{
  className?: string
}>`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.button.secondaryBorder};
`

const StyledSeparator = styled(Separator)`
  background-color: ${({ theme }) => theme.colors.button.secondaryBorder};
`

const SeparatorContainer = styled.div`
  width: 100%;
  padding: 0 16px;
`

export interface ListOption {
  id: string
  label: React.ReactNode
  value: string
}

interface Props extends ListboxProps {
  className?: string
  initialValue?: ListOption
  label?: React.ReactElement
  options: ListOption[]
  onSelect?: (selected: ListOption) => void
}

export const ListBox: React.FC<Props> = ({
  options,
  initialValue,
  onSelect,
  className,
  label,
}) => {
  const [selected, setSelected] = useState(initialValue ?? options[0])
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = (value: ListOption) => {
    setSelected(value)
    handleToggle()
    onSelect?.(value)
  }

  const handleToggle = () => {
    setOpen((state) => !state)
  }

  const selectorId = useMemo(() => {
    return `selector-id-${nanoid()}`
  }, [])

  return (
    <Container>
      {label ? <label htmlFor={selectorId}>{label}</label> : null}
      <StyledListbox
        id={selectorId}
        onChange={handleSelect}
        className={className}
        value={selected}
        $isOpen={open}
      >
        <StyledButton onClick={handleToggle}>{selected.label}</StyledButton>
        {open && (
          <StyledOptions>
            <SeparatorContainer>
              <StyledSeparator />
            </SeparatorContainer>
            {options.map((option) => (
              <TypedListboxOption key={option.id} value={option}>
                <OptionContent>{option.label}</OptionContent>
              </TypedListboxOption>
            ))}
          </StyledOptions>
        )}
      </StyledListbox>
    </Container>
  )
}
